import { logger } from '@inboundx/shared/platform';
import { useQueryClient } from '@tanstack/react-query';
import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { toast } from 'sonner';

import { useWorkspace } from '@/contexts/WorkspaceContext';
import {
    ACTIVE_KNOWLEDGE_OPERATION_STATUSES,
    type CreateKnowledgeOperationCommand,
    canRetryFailedItems,
    graphCountsForOperation,
    type KnowledgeOperation,
    type KnowledgeOperationCreated,
    type KnowledgeOperationTrackHint,
    type KnowledgeOperationType,
    type KnowledgeProgressOwner,
    type KnowledgeProgressScope,
    type KnowledgeWorkItem,
    progressScopeForHint,
    progressScopeForOperation,
    workItemFailureReason,
} from '@/domain/knowledgeOperations';
import { queryKeys } from '@/lib/queryKeys';
import {
    cancelKnowledgeOperation,
    createKnowledgeOperation,
    getKnowledgeOperation,
    listKnowledgeOperationItems,
    listKnowledgeOperations,
    retryKnowledgeOperation,
} from '@/services/knowledgeOperations';

const POLL_INTERVAL_MS = 2_000;
const MAX_BACKOFF_MS = 10_000;

export interface TrackedOperation {
    id: string;
    hint: KnowledgeOperationTrackHint;
    operation: KnowledgeOperation | null;
    pollFailures: number;
}

interface ScopeOwner {
    owner: KnowledgeProgressOwner;
    operationId: string | null;
}

type ScopeOwners = Record<KnowledgeProgressScope, ScopeOwner>;

interface KnowledgeOperationProgressValue {
    operations: KnowledgeOperation[];
    activeOperations: KnowledgeOperation[];
    /** In-flight operations for the selected workspace. */
    activeEntries: TrackedOperation[];
    isRestoring: boolean;
    /** The last restore attempt failed; it keeps retrying in the background. */
    restoreFailed: boolean;
    startOperation: (
        command: CreateKnowledgeOperationCommand
    ) => Promise<KnowledgeOperationCreated>;
    trackOperation: (
        created: KnowledgeOperationCreated,
        hint: KnowledgeOperationTrackHint
    ) => Promise<void>;
    retryOperation: (operationId: string) => Promise<KnowledgeOperationCreated>;
    cancelOperation: (operationId: string) => Promise<KnowledgeOperation>;
    progressOwner: (scope: KnowledgeProgressScope) => KnowledgeProgressOwner;
    claimLegacyProgress: (scope: KnowledgeProgressScope) => void;
    operationForScope: (scope: KnowledgeProgressScope) => KnowledgeOperation | null;
    isOperationActive: (scope: KnowledgeProgressScope) => boolean;
}

const KnowledgeOperationProgressContext = createContext<KnowledgeOperationProgressValue | null>(
    null
);

function legacyScopeOwners(): ScopeOwners {
    return {
        website: { owner: 'legacy', operationId: null },
        managed: { owner: 'legacy', operationId: null },
        knowledge_faq: { owner: 'legacy', operationId: null },
    };
}

function hintFromOperation(operation: KnowledgeOperation): KnowledgeOperationTrackHint {
    return {
        workspaceId: operation.site_id,
        operationType: operation.operation_type,
        sourceScope: operation.source_scope,
    };
}

function operationToastId(operationId: string): string {
    return `knowledge-operation:${operationId}`;
}

// Failure toasts stay until closed, and every reload restores the latest failure.
// A toast the user closed must not come back, so their dismissal is remembered
// per operation (IX-5286). Capped so the list cannot grow without bound.
const DISMISSED_FAILURES_KEY = 'knowledge-operation-dismissed-failures';
const DISMISSED_FAILURES_LIMIT = 50;

function readDismissedFailures(): string[] {
    try {
        const parsed: unknown = JSON.parse(localStorage.getItem(DISMISSED_FAILURES_KEY) ?? '[]');
        return Array.isArray(parsed)
            ? parsed.filter((id): id is string => typeof id === 'string')
            : [];
    } catch {
        return [];
    }
}

function rememberDismissedFailure(operationId: string): void {
    try {
        const ids = [...readDismissedFailures().filter((id) => id !== operationId), operationId];
        localStorage.setItem(
            DISMISSED_FAILURES_KEY,
            JSON.stringify(ids.slice(-DISMISSED_FAILURES_LIMIT))
        );
    } catch {
        // Storage can be unavailable (private mode, quota); the toast may come back.
    }
}

// sonner runs `onDismiss` for programmatic dismissals too (workspace switch,
// unmount), which are not the user closing the toast. Those are marked here first.
const programmaticDismissals = new Set<string>();

function dismissWithoutRemembering(toastId: string): void {
    programmaticDismissals.add(toastId);
    toast.dismiss(toastId);
}

const PHASE_LABELS: Record<string, string> = {
    map: 'Discovering website pages',
    triage: 'Reviewing website sections',
    finalize_discovery: 'Finalizing website selection',
    sample: 'Reading representative pages',
    decide_section: 'Selecting useful pages',
    scrape: 'Fetching website content',
    clean: 'Preparing content for the agent',
    document_loader_job: 'Refreshing agent knowledge',
};

// Sub-phases the loader job reports while it owns document_loader_job. Only the
// ones worth a distinct wording are listed; anything else falls back to the
// phase label so a new backend phase never renders a raw slug.
const LOADER_PHASE_LABELS: Record<string, string> = {
    waiting_for_environment_lock: 'Waiting for an available server',
    // Old copies of changed pages are removed one at a time before the new ones
    // are indexed, which takes minutes on a large site with no count to show.
    replacing_documents: 'Replacing previous versions of updated pages',
    removing_documents: 'Removing pages you excluded',
};

// Which slot a restored operation competes for. A scrape-scoped retrain re-ingests
// the website, so it shares the website slot: a live one must hide the failed
// website run it is retrying (IX-5286). Progress ownership still files it under
// `managed`, so this is deliberately not `progressScopeForOperation`.
function restoreScopeKey(operation: KnowledgeOperation): string {
    if (operation.operation_type === 'website_rescan') return 'rescan';
    return operation.source_scope === 'scrape' ? 'website' : progressScopeForOperation(operation);
}

function loaderPhaseLabel(operation: KnowledgeOperation): string | undefined {
    const { loader_phase: phase, loader_total: total } = operation;
    if (!phase) return undefined;
    // The loader reports `syncing_documents` with no count when the run found
    // nothing to re-ingest: a deletion whose whole job is orphan cleanup, or a
    // retrain where every document already carries the current content hash.
    // Nothing separates the two on the wire, so say the one thing true of both —
    // and say it, because "Refreshing agent knowledge" against a bar that will
    // never move reads as stuck (IX-4710).
    if (phase === 'syncing_documents' && !total) return 'No new documents to index';
    return LOADER_PHASE_LABELS[phase];
}

// Pipeline order for the "Step N/M" hint, per operation type. A full refresh
// traverses every phase; a selected refresh or policy change skips discovery
// and runs scrape → clean → loader. A retrain runs the loader alone, so it gets
// no number. triage / finalize_discovery can still be skipped, so a length is
// the max — fine for a hint, and a fixed length keeps the number from jumping
// backwards.
const FULL_REFRESH_PHASES = [
    'map',
    'triage',
    'finalize_discovery',
    'sample',
    'decide_section',
    'scrape',
    'clean',
    'document_loader_job',
];
const PAGE_REFRESH_PHASES = ['scrape', 'clean', 'document_loader_job'];
const PHASE_ORDERS: Partial<Record<KnowledgeOperationType, string[]>> = {
    website_full_refresh: FULL_REFRESH_PHASES,
    website_selected_refresh: PAGE_REFRESH_PHASES,
    website_policy_change: PAGE_REFRESH_PHASES,
};

function phaseStepLabel(operation: KnowledgeOperation): string | null {
    const order = PHASE_ORDERS[operation.operation_type];
    if (!order || !operation.current_phase) return null;
    const index = order.indexOf(operation.current_phase);
    return index < 0 ? null : `Step ${index + 1}/${order.length}`;
}

export function operationTitle(entry: TrackedOperation): string {
    const type = entry.operation?.operation_type ?? entry.hint.operationType;
    if (type === 'website_rescan') return 'Re-scanning website';
    if (type === 'website_full_refresh') return 'Updating website knowledge';
    if (type === 'website_selected_refresh') return 'Refreshing selected website pages';
    if (type === 'website_policy_change') return 'Updating website knowledge';
    if (type === 'knowledge_mode_migration') return 'Upgrading to advanced knowledge';
    return 'Retraining agent';
}

export function operationProgressLabel(entry: TrackedOperation): string {
    if (entry.pollFailures > 0) {
        return 'Progress is temporarily unavailable. Retrying…';
    }
    const operation = entry.operation;
    if (!operation) return 'Starting…';
    const base =
        loaderPhaseLabel(operation) ??
        (operation.current_phase
            ? (PHASE_LABELS[operation.current_phase] ?? 'Refreshing agent knowledge')
            : operation.status === 'queued'
              ? 'Waiting to start'
              : 'Refreshing agent knowledge');
    const step = phaseStepLabel(operation);
    return step ? `${step} · ${base}` : base;
}

function graphCountsSuffix(operation: KnowledgeOperation): string {
    const counts = graphCountsForOperation(operation);
    if (!counts) return '';
    const parts: string[] = [];
    if (counts.entities != null) parts.push(`${counts.entities.toLocaleString()} entities`);
    if (counts.relationships != null)
        parts.push(`${counts.relationships.toLocaleString()} relationships`);
    if (counts.chunks != null) parts.push(`${counts.chunks.toLocaleString()} chunks`);
    return parts.length ? ` Knowledge graph now holds ${parts.join(' · ')}.` : '';
}

function terminalDescription(operation: KnowledgeOperation): string {
    if (operation.operation_type === 'website_rescan') {
        if (operation.outcome === 'no_changes') {
            return `No added or missing pages were found for ${operation.site_domain}.`;
        }
        if (operation.status === 'succeeded') {
            return `Review the scan for ${operation.site_domain}, then approve updating your agent's knowledge.`;
        }
        if (operation.status === 'cancelled') {
            return `The website re-scan for ${operation.site_domain} was cancelled. Your knowledge was not changed.`;
        }
        return `The website re-scan for ${operation.site_domain} could not finish. Start a fresh re-scan to try again.`;
    }
    if (operation.outcome === 'no_changes') {
        return `No knowledge changed for ${operation.site_domain}, so no indexing job was needed.`;
    }
    if (operation.status === 'succeeded') {
        return `The site-wide indexing job completed for ${operation.site_domain}. Individual documents are not verified separately yet.${graphCountsSuffix(operation)}`;
    }
    if (operation.status === 'partial_failed') {
        return `Valid knowledge was refreshed for ${operation.site_domain}, but some items need another try.${graphCountsSuffix(operation)}`;
    }
    if (operation.status === 'cancelled') {
        return `The knowledge refresh for ${operation.site_domain} was cancelled.`;
    }
    return `The knowledge refresh for ${operation.site_domain} could not finish. Try it again.`;
}

function boundedLabel(value: string, maxLength = 180): string {
    const normalized = value.trim();
    return normalized.length <= maxLength ? normalized : `${normalized.slice(0, maxLength - 1)}…`;
}

function failedWorkItemSubject(item: KnowledgeWorkItem): string {
    const url = item.subject.url;
    if (typeof url === 'string' && url.trim()) return boundedLabel(url);
    const title = item.subject.title;
    if (typeof title === 'string' && title.trim()) return boundedLabel(title);
    if (item.document_id) return `Document ${item.document_id}`;
    if (item.group_key) return `Group ${boundedLabel(item.group_key)}`;
    return 'Knowledge item';
}

function failedWorkItemDescription(
    operation: KnowledgeOperation,
    items: KnowledgeWorkItem[],
    hasMore: boolean
): string {
    const item = items[0];
    if (!item) return terminalDescription(operation);
    const phase = PHASE_LABELS[item.stage] ?? item.stage.split('_').join(' ');
    const reason = workItemFailureReason(item);
    const remaining = Math.max(items.length - 1, 0);
    const more =
        remaining > 0 || hasMore
            ? ` ${remaining > 0 ? remaining : 1}${hasMore ? '+' : ''} more failed item${remaining === 1 && !hasMore ? '' : 's'}.`
            : '';
    return `${failedWorkItemSubject(item)} · Stage: ${phase} (${item.stage}). Reason: ${reason}${more}`;
}

function isTrackedOperationActive(entry: TrackedOperation): boolean {
    return !entry.operation || ACTIVE_KNOWLEDGE_OPERATION_STATUSES.has(entry.operation.status);
}

function shouldPreferScopeOwner(candidate: TrackedOperation, current: TrackedOperation): boolean {
    if (!candidate.operation) return true;
    if (!current.operation) return false;
    if (candidate.operation.created_at !== current.operation.created_at) {
        return candidate.operation.created_at > current.operation.created_at;
    }
    if (candidate.operation.updated_at !== current.operation.updated_at) {
        return candidate.operation.updated_at > current.operation.updated_at;
    }
    return candidate.id > current.id;
}

/** The operation a single-slot surface should show: newest wins, exactly as
 *  scope ownership resolves it. */
export function preferredTrackedOperation(entries: TrackedOperation[]): TrackedOperation | null {
    return entries.reduce<TrackedOperation | null>(
        (best, entry) => (best && !shouldPreferScopeOwner(entry, best) ? best : entry),
        null
    );
}

function ownersFromTrackedOperations(
    tracked: Map<string, TrackedOperation>,
    selectedWorkspaceId: string
): ScopeOwners {
    const owners = legacyScopeOwners();
    for (const entry of tracked.values()) {
        if (entry.hint.workspaceId !== selectedWorkspaceId || !isTrackedOperationActive(entry))
            continue;
        const scope = entry.operation
            ? progressScopeForOperation(entry.operation)
            : progressScopeForHint(entry.hint);
        const currentId = owners[scope].operationId;
        const current = currentId ? tracked.get(currentId) : undefined;
        if (current && !shouldPreferScopeOwner(entry, current)) continue;
        owners[scope] = { owner: 'knowledge_operation', operationId: entry.id };
    }
    return owners;
}

export function KnowledgeOperationProgressProvider({ children }: { children: ReactNode }) {
    const { selectedWorkspaceId } = useWorkspace();
    const queryClient = useQueryClient();
    const [tracked, setTracked] = useState<Map<string, TrackedOperation>>(() => new Map());
    const [restoredWorkspaceId, setRestoredWorkspaceId] = useState<string | null>(null);
    const [restoreFailed, setRestoreFailed] = useState(false);
    // Derive this from the domain whose operations were actually restored. An
    // effect-driven boolean starts false for one committed render, which lets
    // consumers briefly treat an active operation as idle on mount or account
    // switch.
    const isRestoring = !!selectedWorkspaceId && restoredWorkspaceId !== selectedWorkspaceId;
    const trackedRef = useRef(tracked);
    trackedRef.current = tracked;
    const selectedWorkspaceIdRef = useRef(selectedWorkspaceId);
    selectedWorkspaceIdRef.current = selectedWorkspaceId;
    const isMountedRef = useRef(true);
    const notifiedTerminalRef = useRef(new Set<string>());
    const visibleToastIdsRef = useRef(new Set<string>());

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        let retryTimer: ReturnType<typeof setTimeout> | undefined;
        let failureCount = 0;

        setTracked(new Map());
        setRestoredWorkspaceId(null);
        setRestoreFailed(false);
        notifiedTerminalRef.current.clear();
        if (!selectedWorkspaceId) return;

        const restore = async () => {
            try {
                const response = await listKnowledgeOperations(selectedWorkspaceId, 'active');
                if (cancelled) return;
                const restored = new Map<string, TrackedOperation>();
                for (const operation of response.items) {
                    if (operation.site_id !== selectedWorkspaceId) continue;
                    restored.set(operation.id, {
                        id: operation.id,
                        hint: hintFromOperation(operation),
                        operation,
                        pollFailures: 0,
                    });
                }
                setTracked(restored);
                setRestoredWorkspaceId(selectedWorkspaceId);
                setRestoreFailed(false);
                // Keep the latest result for each training scope, including success,
                // so the Knowledge bar can show the outcome after a reload. A
                // discovery-only rescan must not replace a training result.
                void listKnowledgeOperations(selectedWorkspaceId, 'terminal')
                    .then((terminal) => {
                        if (cancelled) return;
                        // Scopes with an operation still in flight own the bar via
                        // their live progress. Their latest terminal record is
                        // typically the failed run a retry is replacing; restoring
                        // it would re-fire the persistent "refresh failed · Retry"
                        // toast beside the live progress, and its Retry would start a
                        // second retry of the same items. Skip those scopes entirely.
                        const activeScopeKeys = new Set<string>();
                        for (const operation of response.items) {
                            if (
                                operation.site_id !== selectedWorkspaceId ||
                                !ACTIVE_KNOWLEDGE_OPERATION_STATUSES.has(operation.status)
                            )
                                continue;
                            activeScopeKeys.add(restoreScopeKey(operation));
                        }
                        const latestByScope = new Map<string, KnowledgeOperation>();
                        for (const operation of terminal.items) {
                            if (operation.site_id !== selectedWorkspaceId) continue;
                            const key = restoreScopeKey(operation);
                            if (activeScopeKeys.has(key)) continue;
                            const previous = latestByScope.get(key);
                            if (!previous || operation.created_at > previous.created_at)
                                latestByScope.set(key, operation);
                        }
                        // Restoring history is not a new completion notification.
                        for (const operation of latestByScope.values()) {
                            if (
                                operation.status === 'succeeded' ||
                                operation.status === 'cancelled'
                            )
                                notifiedTerminalRef.current.add(operation.id);
                        }
                        setTracked((current) => {
                            const next = new Map(current);
                            for (const operation of latestByScope.values()) {
                                if (next.has(operation.id)) continue;
                                next.set(operation.id, {
                                    id: operation.id,
                                    hint: hintFromOperation(operation),
                                    operation,
                                    pollFailures: 0,
                                });
                            }
                            return next;
                        });
                    })
                    .catch(() => undefined);
            } catch (error) {
                if (cancelled) return;
                failureCount += 1;
                // Background restore on every page, retried below: no global toast.
                // The Knowledge bar explains it inline (`restoreFailed`).
                logger.warn('Knowledge progress restore failed; retrying.', error);
                setRestoreFailed(true);
                const delay = Math.min(
                    POLL_INTERVAL_MS * 2 ** Math.min(failureCount - 1, 3),
                    MAX_BACKOFF_MS
                );
                retryTimer = setTimeout(() => void restore(), delay);
            }
        };
        void restore();

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [selectedWorkspaceId]);

    const trackOperation = useCallback(
        async (created: KnowledgeOperationCreated, hint: KnowledgeOperationTrackHint) => {
            if (hint.workspaceId !== selectedWorkspaceId) return;
            notifiedTerminalRef.current.delete(created.operation_id);
            setTracked((current) => {
                const next = new Map(current);
                next.set(created.operation_id, {
                    id: created.operation_id,
                    hint,
                    operation: null,
                    pollFailures: 0,
                });
                return next;
            });

            try {
                const operation = await getKnowledgeOperation(
                    created.operation_id,
                    hint.workspaceId
                );
                if (
                    operation.site_id !== hint.workspaceId ||
                    selectedWorkspaceIdRef.current !== hint.workspaceId
                )
                    return;
                setTracked((current) => {
                    const existing = current.get(created.operation_id);
                    if (!existing || existing.hint.workspaceId !== selectedWorkspaceId)
                        return current;
                    const next = new Map(current);
                    next.set(created.operation_id, {
                        ...existing,
                        hint: hintFromOperation(operation),
                        operation,
                        pollFailures: 0,
                    });
                    return next;
                });
            } catch {
                setTracked((current) => {
                    const existing = current.get(created.operation_id);
                    if (!existing) return current;
                    const next = new Map(current);
                    next.set(created.operation_id, {
                        ...existing,
                        pollFailures: existing.pollFailures + 1,
                    });
                    return next;
                });
            }
        },
        [selectedWorkspaceId]
    );

    const startOperation = useCallback(
        async (command: CreateKnowledgeOperationCommand) => {
            const created = await createKnowledgeOperation(command);
            await trackOperation(created, {
                workspaceId: command.workspace_id,
                operationType: command.type,
                sourceScope: command.source_scope ?? 'scrape',
            });
            return created;
        },
        [trackOperation]
    );

    const retryOperation = useCallback(
        async (operationId: string) => {
            const existing = trackedRef.current.get(operationId);
            const hint =
                existing?.hint ??
                hintFromOperation(await getKnowledgeOperation(operationId, selectedWorkspaceId));
            const created = await retryKnowledgeOperation(operationId, selectedWorkspaceId);
            await trackOperation(created, hint);
            return created;
        },
        [trackOperation, selectedWorkspaceId]
    );

    const cancelOperation = useCallback(
        async (operationId: string) => {
            const operation = await cancelKnowledgeOperation(operationId, selectedWorkspaceId);
            setTracked((current) => {
                const existing = current.get(operationId);
                if (!existing) return current;
                const next = new Map(current);
                next.set(operationId, {
                    ...existing,
                    hint: hintFromOperation(operation),
                    operation,
                    pollFailures: 0,
                });
                return next;
            });
            return operation;
        },
        [selectedWorkspaceId]
    );

    const activeOperationIdsKey = useMemo(() => {
        const ids: string[] = [];
        for (const entry of tracked.values()) {
            if (
                entry.hint.workspaceId === selectedWorkspaceId &&
                (!entry.operation ||
                    ACTIVE_KNOWLEDGE_OPERATION_STATUSES.has(entry.operation.status))
            ) {
                ids.push(entry.id);
            }
        }
        return ids.sort().join(',');
    }, [selectedWorkspaceId, tracked]);

    useEffect(() => {
        if (!activeOperationIdsKey) return;
        const operationIds = activeOperationIdsKey.split(',');
        let cancelled = false;
        let pollTimer: ReturnType<typeof setTimeout> | undefined;
        let failureCount = 0;

        const poll = async () => {
            const results = await Promise.allSettled(
                operationIds.map((operationId) =>
                    getKnowledgeOperation(operationId, selectedWorkspaceId)
                )
            );
            if (cancelled) return;
            let hadFailure = false;
            setTracked((current) => {
                const next = new Map(current);
                for (const [index, result] of results.entries()) {
                    const operationId = operationIds[index];
                    if (!operationId) continue;
                    const existing = next.get(operationId);
                    if (!existing) continue;
                    if (
                        result.status === 'fulfilled' &&
                        result.value.site_id === selectedWorkspaceId
                    ) {
                        next.set(operationId, {
                            ...existing,
                            hint: hintFromOperation(result.value),
                            operation: result.value,
                            pollFailures: 0,
                        });
                    } else {
                        hadFailure = true;
                        next.set(operationId, {
                            ...existing,
                            pollFailures: existing.pollFailures + 1,
                        });
                    }
                }
                return next;
            });

            failureCount = hadFailure ? failureCount + 1 : 0;
            const delay = hadFailure
                ? Math.min(POLL_INTERVAL_MS * 2 ** Math.min(failureCount, 3), MAX_BACKOFF_MS)
                : POLL_INTERVAL_MS;
            pollTimer = setTimeout(() => void poll(), delay);
        };

        pollTimer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            if (pollTimer) clearTimeout(pollTimer);
        };
    }, [activeOperationIdsKey, selectedWorkspaceId]);

    const retryRef = useRef(retryOperation);
    retryRef.current = retryOperation;
    const retryWithFeedback = useCallback((operationId: string) => {
        void retryRef.current(operationId).catch(() => {
            const entry = trackedRef.current.get(operationId);
            if (!isMountedRef.current || entry?.hint.workspaceId !== selectedWorkspaceIdRef.current)
                return;
            toast.error("Couldn't start retry", {
                id: `knowledge-operation-retry:${operationId}`,
                description: 'Rose could not create the retry operation. Try again.',
            });
        });
    }, []);

    useEffect(() => {
        const currentToastIds = new Set<string>();
        for (const entry of tracked.values()) {
            if (entry.hint.workspaceId !== selectedWorkspaceId) continue;
            const operation = entry.operation;
            if (!operation || ACTIVE_KNOWLEDGE_OPERATION_STATUSES.has(operation.status)) {
                continue;
            }
            const toastId = operationToastId(entry.id);
            currentToastIds.add(toastId);
            if (notifiedTerminalRef.current.has(entry.id)) continue;
            notifiedTerminalRef.current.add(entry.id);
            void queryClient.invalidateQueries({
                queryKey: queryKeys.websiteSync.graphCounts(operation.site_id),
            });
            if (operation.source_scope === 'scrape') {
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.websiteSync.unfinishedDocuments(operation.site_id),
                });
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.websiteSync.rescanReviewsAll(operation.site_id),
                });
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.websiteSync.runs(operation.site_id),
                });
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.websiteSync.pagesAll(operation.site_id),
                });
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.websiteSync.lastIngestion(operation.site_id),
                });
            }
            // Only the toast is suppressed: another tab's dismissal must not skip
            // the query refreshes above.
            if (readDismissedFailures().includes(entry.id)) continue;
            programmaticDismissals.delete(toastId);
            const options = {
                id: toastId,
                description: terminalDescription(operation),
            };
            // Only the persistent failure toasts pass this, so only they are remembered.
            const rememberOnClose = () => {
                if (programmaticDismissals.delete(toastId)) return;
                rememberDismissedFailure(entry.id);
            };
            if (operation.operation_type === 'website_rescan') {
                if (operation.status === 'succeeded') {
                    toast.success(
                        operation.outcome === 'no_changes'
                            ? 'No URL changes found'
                            : 'Ready to update knowledge',
                        options
                    );
                } else if (operation.status === 'failed') {
                    toast.error('Website re-scan failed', options);
                } else {
                    toast.info('Website re-scan cancelled', options);
                }
                continue;
            }
            if (operation.status === 'succeeded') {
                toast.success(
                    operation.outcome === 'no_changes'
                        ? 'Agent knowledge is already up to date'
                        : 'Agent knowledge refresh finished',
                    options
                );
            } else if (operation.status === 'partial_failed') {
                const title = 'Agent knowledge was partially refreshed';
                const retryAction = {
                    label: 'Retry failed items',
                    onClick: () => retryWithFeedback(entry.id),
                };
                toast.warning(title, {
                    ...options,
                    action: retryAction,
                    // Keep the manual retry reachable: the default 8s Toaster
                    // dismiss (main.tsx) would otherwise leave a dead-end gate,
                    // and notifiedTerminalRef blocks re-showing this id.
                    duration: Number.POSITIVE_INFINITY,
                    onDismiss: rememberOnClose,
                });
                void listKnowledgeOperationItems(entry.id, {
                    status: 'failed',
                    workspaceId: operation.site_id,
                })
                    .then((response) => {
                        if (
                            !isMountedRef.current ||
                            selectedWorkspaceIdRef.current !== operation.site_id ||
                            !trackedRef.current.has(entry.id) ||
                            readDismissedFailures().includes(entry.id)
                        )
                            return;
                        toast.warning(title, {
                            id: toastId,
                            description: failedWorkItemDescription(
                                operation,
                                response.items,
                                response.next_cursor !== null
                            ),
                            action: canRetryFailedItems(
                                response.items,
                                response.next_cursor !== null
                            )
                                ? retryAction
                                : undefined,
                            duration: Number.POSITIVE_INFINITY,
                            onDismiss: rememberOnClose,
                        });
                    })
                    .catch(() => undefined);
            } else if (operation.status === 'failed') {
                const title = 'Agent knowledge refresh failed';
                const retryAction = {
                    label: 'Retry',
                    onClick: () => retryWithFeedback(entry.id),
                };
                toast.error(title, {
                    ...options,
                    action: retryAction,
                    // Keep the manual retry reachable across the 8s Toaster
                    // auto-dismiss (main.tsx); notifiedTerminalRef blocks a
                    // repeat for this id after a refresh restores the failure.
                    duration: Number.POSITIVE_INFINITY,
                    onDismiss: rememberOnClose,
                });
                void listKnowledgeOperationItems(entry.id, {
                    status: 'failed',
                    workspaceId: operation.site_id,
                })
                    .then((response) => {
                        if (
                            !isMountedRef.current ||
                            selectedWorkspaceIdRef.current !== operation.site_id ||
                            !trackedRef.current.has(entry.id) ||
                            readDismissedFailures().includes(entry.id)
                        )
                            return;
                        toast.error(title, {
                            id: toastId,
                            description: failedWorkItemDescription(
                                operation,
                                response.items,
                                response.next_cursor !== null
                            ),
                            action: canRetryFailedItems(
                                response.items,
                                response.next_cursor !== null
                            )
                                ? retryAction
                                : undefined,
                            duration: Number.POSITIVE_INFINITY,
                            onDismiss: rememberOnClose,
                        });
                    })
                    .catch(() => undefined);
            } else {
                toast.info('Agent knowledge refresh cancelled', options);
            }
        }

        for (const toastId of visibleToastIdsRef.current) {
            if (!currentToastIds.has(toastId)) dismissWithoutRemembering(toastId);
        }
        visibleToastIdsRef.current = currentToastIds;
    }, [queryClient, retryWithFeedback, tracked, selectedWorkspaceId]);

    useEffect(
        () => () => {
            for (const toastId of visibleToastIdsRef.current) dismissWithoutRemembering(toastId);
        },
        []
    );

    const operations = useMemo(
        () =>
            Array.from(tracked.values()).flatMap((entry) =>
                entry.operation?.site_id === selectedWorkspaceId ? [entry.operation] : []
            ),
        [tracked, selectedWorkspaceId]
    );
    const activeOperations = useMemo(
        () =>
            operations.filter((operation) =>
                ACTIVE_KNOWLEDGE_OPERATION_STATUSES.has(operation.status)
            ),
        [operations]
    );
    const activeEntries = useMemo(
        () =>
            Array.from(tracked.values()).filter(
                (entry) =>
                    entry.hint.workspaceId === selectedWorkspaceId &&
                    (!entry.operation ||
                        ACTIVE_KNOWLEDGE_OPERATION_STATUSES.has(entry.operation.status))
            ),
        [selectedWorkspaceId, tracked]
    );
    const scopeOwners = useMemo(
        () => ownersFromTrackedOperations(tracked, selectedWorkspaceId),
        [selectedWorkspaceId, tracked]
    );

    const progressOwner = useCallback(
        (scope: KnowledgeProgressScope) => scopeOwners[scope].owner,
        [scopeOwners]
    );
    const operationForScope = useCallback(
        (scope: KnowledgeProgressScope) => {
            const operationId = scopeOwners[scope].operationId;
            return operationId ? (tracked.get(operationId)?.operation ?? null) : null;
        },
        [scopeOwners, tracked]
    );
    const isOperationActive = useCallback(
        (scope: KnowledgeProgressScope) => {
            const operationId = scopeOwners[scope].operationId;
            if (!operationId || scopeOwners[scope].owner !== 'knowledge_operation') return false;
            const operation = tracked.get(operationId)?.operation;
            return !operation || ACTIVE_KNOWLEDGE_OPERATION_STATUSES.has(operation.status);
        },
        [scopeOwners, tracked]
    );
    const claimLegacyProgress = useCallback((scope: KnowledgeProgressScope) => {
        const currentOwner = ownersFromTrackedOperations(
            trackedRef.current,
            selectedWorkspaceIdRef.current
        )[scope];
        if (currentOwner.owner === 'knowledge_operation') return;
        // With no active operation, derived ownership is already legacy.
    }, []);

    const value = useMemo(
        () => ({
            operations,
            activeOperations,
            activeEntries,
            isRestoring,
            restoreFailed,
            startOperation,
            trackOperation,
            retryOperation,
            cancelOperation,
            progressOwner,
            claimLegacyProgress,
            operationForScope,
            isOperationActive,
        }),
        [
            operations,
            activeOperations,
            activeEntries,
            isRestoring,
            restoreFailed,
            startOperation,
            trackOperation,
            retryOperation,
            cancelOperation,
            progressOwner,
            claimLegacyProgress,
            operationForScope,
            isOperationActive,
        ]
    );

    return (
        <KnowledgeOperationProgressContext.Provider value={value}>
            {children}
        </KnowledgeOperationProgressContext.Provider>
    );
}

export function useKnowledgeOperationProgress(): KnowledgeOperationProgressValue {
    const context = useContext(KnowledgeOperationProgressContext);
    if (!context) {
        throw new Error(
            'useKnowledgeOperationProgress must be used within KnowledgeOperationProgressProvider'
        );
    }
    return context;
}
